import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/site";
import { Reveal, SectionLabel, GlowButton } from "@/components/primitives";

const FRAMES = [
  { title: "Product Spot", bg: "linear-gradient(160deg,#FF6B35,#FF9F6B)", shape: "circle" },
  { title: "Social Reel", bg: "linear-gradient(160deg,#7C3AED,#C026D3)", shape: "blob" },
  { title: "Brand Film", bg: "linear-gradient(160deg,#2563EB,#7C3AED)", shape: "square" },
  { title: "AI Visual", bg: "linear-gradient(160deg,#111827,#334155)", shape: "ring" },
];

export default function VideoAds() {
  return (
    <section className="relative overflow-hidden py-20 sm:py-28 lg:py-32" data-testid="video-ads-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <SectionLabel color="#FF6B35">AI Video Ads</SectionLabel>
          <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
            Imagine your product
            <br />
            <span className="text-gradient-warm">in motion.</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Scroll-stopping video creative built with AI — from short-form reels to cinematic brand
            spots, in multiple variations ready to test.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 gap-5 sm:gap-6 lg:grid-cols-4">
          {FRAMES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.1}>
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="group relative overflow-hidden rounded-3xl bg-white p-2.5 shadow-card ring-1 ring-slate-100"
                data-testid={`video-ad-card-${i}`}
              >
                <div className="relative aspect-[9/14] overflow-hidden rounded-2xl" style={{ background: f.bg }}>
                  {/* abstract product shape */}
                  {f.shape === "circle" && <div className="absolute left-1/2 top-[38%] h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/30 backdrop-blur-sm" />}
                  {f.shape === "blob" && <div className="absolute left-1/2 top-[38%] h-24 w-20 -translate-x-1/2 -translate-y-1/2 rounded-[45%_55%_60%_40%] bg-white/30 backdrop-blur-sm" />}
                  {f.shape === "square" && <div className="absolute left-1/2 top-[38%] h-20 w-20 -translate-x-1/2 -translate-y-1/2 rotate-12 rounded-2xl bg-white/30 backdrop-blur-sm" />}
                  {f.shape === "ring" && <div className="absolute left-1/2 top-[38%] h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border-8 border-white/40" />}

                  <div className="absolute left-2.5 top-2.5 rounded-full bg-white/25 px-2 py-0.5 text-[8px] font-bold uppercase tracking-wide text-white backdrop-blur-sm">
                    AI Video Ads
                  </div>
                  <div className="absolute left-1/2 top-1/2 grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white shadow-lg transition-transform group-hover:scale-110">
                    <Play className="h-4.5 w-4.5 translate-x-[1px]" style={{ width: 18, height: 18, fill: "#111827", color: "#111827" }} />
                  </div>
                  <div className="absolute inset-x-3 bottom-8 flex items-end gap-0.5">
                    {[8, 14, 6, 16, 10, 13, 7].map((h, bi) => (
                      <motion.span key={bi} className="w-full rounded-full bg-white/70" animate={{ height: [`${h}px`, `${h + 5}px`, `${h}px`] }} transition={{ duration: 1.2, repeat: Infinity, delay: bi * 0.1 }} style={{ height: `${h}px` }} />
                    ))}
                  </div>
                  <div className="absolute inset-x-3 bottom-3 h-1 overflow-hidden rounded-full bg-white/30">
                    <motion.div className="h-full bg-white" animate={{ width: ["0%", "100%"] }} transition={{ duration: 4, repeat: Infinity, ease: "linear", delay: i * 0.5 }} />
                  </div>
                </div>
                <div className="px-1.5 py-3">
                  <span className="font-display text-sm font-bold text-[#111827]">{f.title}</span>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-12 flex justify-center">
          <GlowButton onClick={() => scrollToId("contact")} data-testid="video-ads-cta">
            Create Your Ad
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </GlowButton>
        </Reveal>
      </div>
    </section>
  );
}
